import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import AsyncTable, { Column, RequestPage } from '../../../../components/AsyncTable';
import { backendErrorNotification } from '../../../../components/Notification';
import { ActionButtonsContainer, ViewIconTable } from '../../../../components/TablePaginator/styles';
import { groupedOrderPendenciesApi } from '../../../../services/requests';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { OrderPendencyGroup } from '../../../../types/apiResponse/orderPendency';
import requireSuccessfulResponse from '../../../../services/utilities/requireSuccessfulResponse';
import { ApiResPaginated } from '../../../../types/apiResponseTypes';
import checkPermission from '../../../../utils/checkPermission';
import OrderDetailsByNumberModal from '../../Orders/Components/OrderDetailsModal/ByNumber';

const personLabel = (person: OrderPendencyGroup['seller']) => (
  [person?.code, person?.name].filter((value) => value !== null && value !== undefined && value !== '').join(' - ') || '-'
);

const OrdersWithPendenciesPage = () => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || 'pt').startsWith('en') ? 'en-US' : 'pt-BR';
  const formatDate = (value: string | null) => {
    if (!value || value.startsWith('0000-')) return '-';
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return value;
    const [, year, month, day] = match;
    return new Intl.DateTimeFormat(locale).format(new Date(Number(year), Number(month) - 1, Number(day)));
  };
  const formatValue = (value: number | null) => (value == null ? '-'
    : new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL' }).format(value));
  const theme = useTheme();
  const [data, setData] = useState<OrderPendencyGroup[]>([]);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<number | null>(null);
  const [refresh, setRefresh] = useState(0);
  const canViewOrder = checkPermission('order-view');

  const columns: Column<OrderPendencyGroup>[] = [
    { label: t('orderNumber') as string, accessor: 'order_number' },
    { label: t('seller') as string, Cell: (row) => personLabel(row.seller) },
    { label: t('customer') as string, Cell: (row) => personLabel(row.customer) },
    { label: t('pendencyOrderDate') as string, Cell: (row) => formatDate(row.order_date) },
    { label: t('value') as string, Cell: (row) => formatValue(row.order_value) },
    { label: t('pendencyOrderItemsCount') as string, Cell: (row) => String(row.items_count ?? '-') },
    { label: t('pendencyTotal') as string, accessor: 'total_pendencies' },
    { label: t('pendenciesApproved') as string, accessor: 'approved' },
    { label: t('pendenciesRejected') as string, accessor: 'rejected' },
    {
      label: t('actions') as string,
      Cell: (row) => canViewOrder ? (
        <ActionButtonsContainer>
          <ViewIconTable title={t('pendencyViewOrder') as string}
            role="button" tabIndex={0} aria-label={`${t('pendencyViewOrder')} ${row.order_number}`}
            onClick={() => setSelectedOrderNumber(row.order_number)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setSelectedOrderNumber(row.order_number);
              }
            }} />
        </ActionButtonsContainer>
      ) : null,
    },
  ];
  const requestPage: RequestPage = async ({ search, page, quantityPerPage }) => {
    try {
      const query = new URLSearchParams({
        search, page: String(page), quantityPerPage: String(quantityPerPage), withOrderSummary: 'true',
      });
      groupedOrderPendenciesApi.query = `?${query.toString()}`;
      const response = await groupedOrderPendenciesApi.index();
      requireSuccessfulResponse(response);
      const result: ApiResPaginated<Record<string, OrderPendencyGroup>> = response.data;
      return {
        data: Object.values(result.data), totalPage: result.paginated.totalPage,
        hasMore: page < result.paginated.totalPage,
      };
    } catch (err) {
      backendErrorNotification(err as AxiosError<any, any>);
      return { data: [], totalPage: 0, hasMore: false };
    }
  };

  return (
    <ContentContainer title={t('ordersWithPendencies') as string}>
      <AsyncTable columns={columns} tableName={t('ordersWithPendencies') as string} value={data} onChange={setData}
        requestPage={requestPage} reqListener={[refresh]} options={{
          styles: {
            primaryColor: theme.colors.surface, secondaryColor: theme.colors.onSurface,
            alternateRowColor: theme.colors.onSurfaceLight, textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage') as string, searchPlaceholder: t('search') as string,
        }} />
      {selectedOrderNumber !== null && (
        <OrderDetailsByNumberModal key={selectedOrderNumber} orderNumber={selectedOrderNumber}
          initialTab="approval" closeModal={() => setSelectedOrderNumber(null)}
          onPendenciesChanged={() => setRefresh((value) => value + 1)} />
      )}
    </ContentContainer>
  );
};

export default OrdersWithPendenciesPage;
